"use client"

/**
 * Topsoil / lawn-leveling calculator island — ported from the Claude Design spec
 * (project 68b86b0d, "Topsoil Calculator.dc.html"). Inline-styled to match the
 * design; wired to the real `calculateTopDressingOrder` engine and the site-wide
 * region resolver. Fully live (no submit gate), like sibling tools.
 *
 * The evergreen SEO sections (cubic-yards-vs-bags table, material comparison,
 * FAQ) are NOT here — they render server-side via ToolContentFooter so they stay
 * crawlable (this island sits behind a useSearchParams Suspense boundary and
 * never reaches the initial HTML).
 */

import { useEffect, useMemo, useRef, useState } from "react"
import { ZIP_INPUT_ATTRS, sanitizeZip } from "@/lib/zip-input"
import {
  MapPin,
  Shovel,
  Truck,
  ShoppingCart,
  Info,
  Ruler,
  AlignEndHorizontal,
  Package,
  ArrowRight,
  ArrowUp,
} from "lucide-react"
import {
  calculateTopDressingOrder,
  fmtDepth,
  TOP_DRESSING_MATERIALS,
  type TopDressingMaterial,
} from "@/lib/top-dressing/calculator"

import { amazonUrl as amazonDp, amazonSearchUrl as amazonSearch } from "@/lib/affiliate"
const amazonImg = (id: string) => `https://m.media-amazon.com/images/I/${id}._AC_SL200_.jpg`

const DEPTHS: { v: number; label: string; sub: string }[] = [
  { v: 0.125, label: "1/8 in", sub: "Seed cover" },
  { v: 0.25, label: "1/4 in", sub: "Standard topdress" },
  { v: 0.375, label: "3/8 in", sub: "Heavy topdress" },
  { v: 0.5, label: "1/2 in", sub: "Leveling pass" },
  { v: 1, label: "1 in", sub: "Fill (grass buried)" },
  { v: 2, label: "2 in", sub: "New bed depth" },
]

const SIZE_PRESETS = [1000, 2500, 5000, 10000]
const AREA_MIN = 100
const AREA_MAX = 30000

// One matched pick per material, rendered inline at the result peak. Coast of
// Maine compost is a PAAPI-verified catalog ASIN; the tool/loose-material picks
// use tagged Amazon search URLs (no fabricated ASINs) until specific SKUs are
// verified — see obsidian/features/topsoil-calculator.md.
interface MaterialPick {
  name: string
  why: string
  url: string
  imageUrl?: string
}
const MATERIAL_PICKS: Record<TopDressingMaterial, MaterialPick> = {
  topsoil: {
    name: "Screened topsoil (bagged)",
    why: "Consistent screened fill for small patch jobs",
    url: amazonSearch("screened topsoil bag lawn"),
  },
  sand: {
    name: "36 in Lawn Leveling Rake (lute)",
    why: "The tool that drags sand into the low spots",
    url: amazonSearch("lawn leveling rake 36 inch lute"),
  },
  compost: {
    name: "Coast of Maine Lawn Topdressing Compost",
    why: "Fine-screened so it settles through the canopy",
    url: amazonDp("B07CM8RRVN"),
    imageUrl: amazonImg("4105k9TbzbL"),
  },
  soil_mix: {
    name: "36 in Lawn Leveling Rake (lute)",
    why: "The tool that drags the mix into the low spots",
    url: amazonSearch("lawn leveling rake 36 inch lute"),
  },
  peat: {
    name: "Espoma Organic Peat Moss",
    why: "Light seed cover that holds moisture",
    url: amazonSearch("espoma organic peat moss"),
  },
}

export interface TopsoilInitialValues {
  zipCode?: string
  lawnSize?: number
  material?: TopDressingMaterial
}

export type TopsoilOrder = ReturnType<typeof calculateTopDressingOrder>

export function TopsoilForm({
  initialValues,
  onResultChange,
}: {
  initialValues?: TopsoilInitialValues | null
  onResultChange?: (order: TopsoilOrder) => void
}) {
  const [material, setMaterial] = useState<TopDressingMaterial>(initialValues?.material ?? "sand")
  const [depth, setDepth] = useState(0.25)
  const [area, setArea] = useState(initialValues?.lawnSize ?? 5000)
  const [zip, setZip] = useState(initialValues?.zipCode ?? "")
  const [narrow, setNarrow] = useState(false)

  const rootRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!initialValues) return
    if (initialValues.material) setMaterial(initialValues.material)
    if (initialValues.zipCode) setZip(initialValues.zipCode)
    if (typeof initialValues.lawnSize === "number") setArea(initialValues.lawnSize)
  }, [initialValues])

  useEffect(() => {
    const el = rootRef.current
    if (!el || typeof ResizeObserver === "undefined") return
    const ro = new ResizeObserver(() => setNarrow(el.getBoundingClientRect().width < 720))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const order = useMemo(
    () => calculateTopDressingOrder({ areaSqFt: area, depthInches: depth, material, zipCode: zip }),
    [area, depth, material, zip],
  )

  useEffect(() => {
    onResultChange?.(order)
  }, [order, onResultChange])

  const depthLabel = fmtDepth(depth)
  const pick = MATERIAL_PICKS[material]
  const cuYdDisplay = order.volumeCubicYards >= 10 ? order.volumeCubicYards.toFixed(0) : order.volumeCubicYards.toFixed(1)
  const region = order.regionKnown ? order.region : null

  const scrollToResults = () => {
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" })
  }

  return (
    <div ref={rootRef}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: narrow ? "1fr" : "repeat(auto-fit, minmax(330px, 1fr))",
          gap: 28,
          alignItems: "start",
        }}
      >
        {/* ============ FORM ============ */}
        <div
          style={{
            borderRadius: 24,
            background: "#fff",
            border: "1px solid #e5e7eb",
            padding: 28,
            boxShadow: "0 12px 40px rgba(0,0,0,0.04)",
          }}
        >
          <FieldLabel>1 · Material</FieldLabel>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 10 }}>
            {TOP_DRESSING_MATERIALS.map((m) => {
              const selected = m.id === material
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setMaterial(m.id)}
                  aria-pressed={selected}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 5,
                    boxSizing: "border-box",
                    padding: "13px 14px",
                    border: selected ? "2px solid #16a34a" : "1px solid #e5e7eb",
                    borderRadius: 16,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    textAlign: "left",
                    background: selected ? "#f0fdf4" : "#fff",
                  }}
                >
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: "#111827" }}>{m.label}</span>
                  <span style={{ fontSize: 11.5, lineHeight: 1.4, color: "#6b7280" }}>{m.sub}</span>
                </button>
              )
            })}
          </div>

          <FieldLabel style={{ marginTop: 24 }}>2 · Depth</FieldLabel>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {DEPTHS.map((d) => {
              const selected = d.v === depth
              return (
                <button
                  key={d.v}
                  type="button"
                  onClick={() => setDepth(d.v)}
                  aria-pressed={selected}
                  style={{
                    display: "inline-flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                    boxSizing: "border-box",
                    minHeight: 48,
                    padding: "7px 16px",
                    border: selected ? "2px solid #16a34a" : "1px solid #e5e7eb",
                    borderRadius: 14,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    background: selected ? "#f0fdf4" : "#fff",
                  }}
                >
                  <span style={{ fontSize: 14, fontWeight: 800, color: "#111827" }}>{d.label}</span>
                  <span style={{ fontSize: 10.5, fontWeight: 600, color: "#6b7280" }}>{d.sub}</span>
                </button>
              )
            })}
          </div>

          <FieldLabel style={{ marginTop: 24 }}>3 · Area to cover</FieldLabel>
          <div style={{ position: "relative" }}>
            <input
              type="number"
              min={AREA_MIN}
              value={area}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10)
                setArea(isNaN(v) ? 0 : Math.max(0, v))
              }}
              aria-label="Area to cover in square feet"
              aria-invalid={area < AREA_MIN}
              style={{
                width: "100%",
                boxSizing: "border-box",
                minHeight: 52,
                padding: "0 74px 0 16px",
                fontFamily: "inherit",
                fontSize: 17,
                fontWeight: 700,
                color: "#111827",
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                background: "#fff",
                outline: "none",
              }}
            />
            <span style={{ position: "absolute", top: "50%", right: 16, transform: "translateY(-50%)", fontSize: 13, fontWeight: 600, color: "#6b7280" }}>
              sq ft
            </span>
          </div>
          {area < AREA_MIN && (
            <p role="alert" style={{ margin: "8px 0 0", fontSize: 13, fontWeight: 600, color: "#b45309" }}>
              Enter at least {AREA_MIN} sq ft for a useful material estimate.
            </p>
          )}
          <input
            type="range"
            min={Math.log10(AREA_MIN)}
            max={Math.log10(AREA_MAX)}
            step={0.01}
            value={Math.log10(Math.min(Math.max(area, AREA_MIN), AREA_MAX))}
            onChange={(e) => setArea(Math.round(Math.pow(10, parseFloat(e.target.value)) / 50) * 50)}
            aria-label="Area slider"
            style={{ width: "100%", marginTop: 12, accentColor: "#16a34a" }}
          />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
            {SIZE_PRESETS.map((v) => {
              const selected = v === area
              return (
                <button
                  key={v}
                  type="button"
                  onClick={() => setArea(v)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    boxSizing: "border-box",
                    minHeight: 36,
                    padding: "0 14px",
                    border: selected ? "2px solid #16a34a" : "1px solid #e5e7eb",
                    borderRadius: 9999,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#111827",
                    background: selected ? "#f0fdf4" : "#fff",
                  }}
                >
                  {v.toLocaleString()} sq ft
                </button>
              )
            })}
          </div>
          <p style={{ margin: "10px 0 0", fontSize: 13, color: "#6b7280" }}>
            Leveling just the low spots? Measure them with the{" "}
            <a href="/tools/lawn-size-calculator" style={{ fontWeight: 600, color: "#16a34a", textDecoration: "underline", textUnderlineOffset: 2 }}>
              lawn size calculator
            </a>{" "}
            and enter that area.
          </p>

          <FieldLabel style={{ marginTop: 24 }}>
            4 · ZIP code{" "}
            <span style={{ fontWeight: 600, textTransform: "none", letterSpacing: 0, color: "#6b7280" }}>(optional, for local pricing)</span>
          </FieldLabel>
          <div style={{ position: "relative" }}>
            <MapPin size={18} style={{ position: "absolute", top: "50%", left: 16, transform: "translateY(-50%)", color: "#9ca3af" }} />
            <input
              {...ZIP_INPUT_ATTRS}
              value={zip}
              onChange={(e) => setZip(sanitizeZip(e.target.value))}
              placeholder="e.g. 30301"
              aria-label="ZIP code"
              style={{
                width: "100%",
                boxSizing: "border-box",
                minHeight: 52,
                padding: "0 150px 0 44px",
                fontFamily: "inherit",
                fontSize: 17,
                fontWeight: 700,
                letterSpacing: "0.04em",
                color: "#111827",
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                background: "#fff",
                outline: "none",
              }}
            />
            <span
              style={{
                position: "absolute",
                top: "50%",
                right: 14,
                transform: "translateY(-50%)",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "4px 12px",
                borderRadius: 9999,
                fontSize: 12,
                fontWeight: 700,
                color: region ? "#166534" : "#6b7280",
                background: region ? "#f0fdf4" : "#f9fafb",
                border: `1px solid ${region ? "#bbf7d0" : "#e5e7eb"}`,
              }}
            >
              {order.regionLabel}
            </span>
          </div>
        </div>

        {/* ============ RESULTS RAIL ============ */}
        <div style={{ position: narrow ? "static" : "sticky", top: 24, display: "flex", flexDirection: "column", gap: 16 }}>
          {area < AREA_MIN ? (
            <div role="status" style={{ borderRadius: 24, padding: 24, background: "#fffbeb", border: "1px solid #fde68a", color: "#78350f", fontSize: 14, lineHeight: 1.6 }}>
              Enter at least {AREA_MIN} sq ft to see cubic yards, bag counts, weight, and cost.
            </div>
          ) : (
          <div style={{ borderRadius: 24, overflow: "hidden", background: "#fff", border: "1px solid #dcfce7", boxShadow: "0 12px 40px rgba(0,0,0,0.06)" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "16px 24px",
                background: "linear-gradient(90deg, #16a34a 0%, #059669 100%)",
                color: "#fff",
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 16, fontWeight: 700 }}>
                <Shovel size={19} />
                Your order
              </span>
              <span style={{ fontSize: 13, fontWeight: 600, opacity: 0.9 }}>
                {order.materialLabel} · {depthLabel}
              </span>
            </div>

            <div style={{ padding: 24 }}>
              {/* big cu yd */}
              <div style={{ padding: 22, borderRadius: 18, background: "#f0fdf4", border: "1px solid #bbf7d0", textAlign: "center" }}>
                <div style={{ fontSize: 44, fontWeight: 800, lineHeight: 1, letterSpacing: "-0.02em", color: "#14532d" }}>{cuYdDisplay} cu yd</div>
                <div style={{ marginTop: 8, fontSize: 14, fontWeight: 600, color: "#166534" }}>
                  of {order.materialWord} at {depthLabel} deep over {order.areaSqFt.toLocaleString()} sq ft
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginTop: 12 }}>
                <StatBox value={Math.ceil(order.volumeCubicFeet).toLocaleString()} label="cubic feet" />
                <StatBox value={order.weightLabel} label="approx. weight" />
                <StatBox value={order.bags40lb.toLocaleString()} label="40 lb bags" />
              </div>

              {/* cost comparison */}
              <SectionLabel style={{ marginTop: 22 }}>Bulk or bagged · {order.regionLabel}</SectionLabel>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div
                  style={{
                    padding: 16,
                    borderRadius: 16,
                    background: order.bulkCheaper ? "#f0fdf4" : "#f9fafb",
                    border: `1px solid ${order.bulkCheaper ? "#bbf7d0" : "#e5e7eb"}`,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Truck size={16} style={{ color: "#16a34a" }} />
                    <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "#166534" }}>Bulk delivery</span>
                  </div>
                  <div style={{ marginTop: 8, fontSize: 20, fontWeight: 800, color: "#14532d" }}>{order.bulkCost.label}</div>
                  <div style={{ marginTop: 4, fontSize: 12.5, lineHeight: 1.5, color: "#166534" }}>{order.bulkNote}</div>
                </div>
                <div style={{ padding: 16, borderRadius: 16, background: "#f9fafb", border: "1px solid #e5e7eb" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <ShoppingCart size={16} style={{ color: "#6b7280" }} />
                    <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "#6b7280" }}>Bagged</span>
                  </div>
                  <div style={{ marginTop: 8, fontSize: 20, fontWeight: 800, color: "#111827" }}>{order.baggedCost.label}</div>
                  <div style={{ marginTop: 4, fontSize: 12.5, lineHeight: 1.5, color: "#6b7280" }}>{order.baggedNote}</div>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginTop: 12, padding: "13px 15px", borderRadius: 13, background: "#f9fafb", border: "1px solid #e5e7eb" }}>
                <Info size={16} style={{ flexShrink: 0, marginTop: 1, color: "#16a34a" }} />
                <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55, color: "#374151" }}>{order.savingsNote}</p>
              </div>

              {/* leveling callout for sand / mix */}
              {order.showLeveling && (
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginTop: 12, padding: "14px 16px", borderRadius: 14, background: "#f0fdf4", border: "1px solid #bbf7d0" }}>
                  <AlignEndHorizontal size={18} style={{ flexShrink: 0, marginTop: 1, color: "#16a34a" }} />
                  <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: "#166534" }}>
                    <strong>Leveling a Bermuda or Zoysia lawn?</strong> Do it in early summer when the grass is growing fast enough to punch through. Apply {depthLabel} max
                    per pass, drag it in with a leveling rake, and repeat monthly rather than burying the lawn once.
                  </p>
                </div>
              )}

              {/* depth guidance */}
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                  marginTop: 12,
                  padding: "13px 15px",
                  borderRadius: 13,
                  background: order.depthWarning ? "#fffbeb" : "#f9fafb",
                  border: `1px solid ${order.depthWarning ? "#fde68a" : "#e5e7eb"}`,
                }}
              >
                <Ruler size={16} style={{ flexShrink: 0, marginTop: 1, color: order.depthWarning ? "#d97706" : "#16a34a" }} />
                <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55, color: order.depthWarning ? "#92400e" : "#374151" }}>{order.depthNote}</p>
              </div>

              {/* product pick */}
              <SectionLabel style={{ marginTop: 20, color: "#6b7280" }}>Matched to your project</SectionLabel>
              <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 14px", borderRadius: 14, background: "#f9fafb", border: "1px solid #e5e7eb" }}>
                <div
                  style={{
                    flexShrink: 0,
                    width: 42,
                    height: 42,
                    borderRadius: 10,
                    overflow: "hidden",
                    background: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {pick.imageUrl ? (
                    <img src={pick.imageUrl} alt={pick.name} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} loading="lazy" />
                  ) : (
                    <Package size={20} style={{ color: "#9ca3af" }} />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 13.5, fontWeight: 700, color: "#111827" }}>{pick.name}</span>
                  <span style={{ display: "block", marginTop: 2, fontSize: 12, color: "#6b7280" }}>{pick.why}</span>
                </div>
                <a
                  href={pick.url}
                  target="_blank"
                  rel="noopener noreferrer nofollow sponsored"
                  data-affiliate="true"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    minHeight: 40,
                    padding: "0 15px",
                    borderRadius: 9999,
                    textDecoration: "none",
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#fff",
                    background: "linear-gradient(90deg, #16a34a 0%, #059669 100%)",
                    whiteSpace: "nowrap",
                    boxShadow: "0 8px 20px -6px rgba(22,163,74,0.30)",
                  }}
                >
                  Check price
                  <ArrowRight size={15} />
                </a>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* mobile bottom dock */}
      {narrow && area >= AREA_MIN && (
        <div
          style={{
            position: "sticky",
            bottom: 0,
            zIndex: 50,
            display: "flex",
            alignItems: "center",
            gap: 14,
            marginTop: 16,
            padding: "12px 16px calc(12px + env(safe-area-inset-bottom))",
            background: "rgba(255,255,255,0.92)",
            backdropFilter: "blur(8px)",
            borderTop: "1px solid #e5e7eb",
            boxShadow: "0 -8px 30px rgba(0,0,0,0.08)",
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {cuYdDisplay} cu yd {order.materialLabel.toLowerCase()} · {order.bags40lb.toLocaleString()} bags
            </div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#15803d" }}>Bulk: {order.bulkCost.label}</div>
          </div>
          <button
            type="button"
            onClick={scrollToResults}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              minHeight: 44,
              padding: "0 18px",
              border: "none",
              borderRadius: 9999,
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 14,
              fontWeight: 700,
              color: "#fff",
              background: "linear-gradient(90deg, #16a34a 0%, #059669 100%)",
              whiteSpace: "nowrap",
            }}
          >
            See my order
            <ArrowUp size={15} />
          </button>
        </div>
      )}
    </div>
  )
}

function StatBox({ value, label }: { value: string; label: string }) {
  return (
    <div style={{ padding: "13px 8px", borderRadius: 14, background: "#f9fafb", border: "1px solid #e5e7eb", textAlign: "center" }}>
      <div style={{ fontSize: 19, fontWeight: 800, lineHeight: 1, color: "#111827" }}>{value}</div>
      <div style={{ marginTop: 5, fontSize: 11.5, fontWeight: 600, color: "#6b7280" }}>{label}</div>
    </div>
  )
}

function FieldLabel({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <p style={{ margin: "0 0 12px", fontSize: 13, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "#15803d", ...style }}>
      {children}
    </p>
  )
}

function SectionLabel({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <p style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "#15803d", ...style }}>
      {children}
    </p>
  )
}
